import React, { useState } from 'react'
import { useT } from '../lib/i18n'
import { ackAlert, type Alert } from '../lib/api'

interface Props {
  alerts: Alert[]
  onAck: () => void
}

const SEV_COLOR: Record<string, string> = {
  critical: 'var(--health-crit)',
  high:     'var(--health-warn)',
  medium:   '#60a5fa',
  low:      'var(--text-muted)',
}

export default function AlertPanel({ alerts, onAck }: Props) {
  const t = useT()
  const [acking, setAcking] = useState<string | null>(null)
  const [showAcked, setShowAcked] = useState(false)

  const visible = showAcked ? alerts : alerts.filter((a) => !a.ackedAt)

  async function handleAck(id: string) {
    setAcking(id)
    try { await ackAlert(id); onAck() }
    finally { setAcking(null) }
  }

  return (
    <aside
      style={{
        width: '260px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          fontSize: '12px', fontWeight: 600,
        }}
      >
        <span style={{ color: 'var(--health-crit)' }}>◉</span>
        {t('alerts')}
        {alerts.filter((a) => !a.ackedAt).length > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              background: 'var(--health-crit)',
              color: '#fff',
              borderRadius: '999px',
              padding: '0 6px',
              fontSize: '10px',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {alerts.filter((a) => !a.ackedAt).length}
          </span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {visible.length === 0 ? (
          <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
            {t('no_alerts')}
          </div>
        ) : (
          visible.map((a) => (
            <div
              key={a.id}
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid var(--border)',
                opacity: a.ackedAt ? 0.45 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SEV_COLOR[a.severity] ?? 'var(--text-muted)', flexShrink: 0, display: 'inline-block' }} />
                <span style={{ fontSize: '10px', color: SEV_COLOR[a.severity], fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {t(`severity_${a.severity}`)}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {a.at.slice(11, 19)}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text)', marginBottom: '4px', lineHeight: 1.4 }}>
                {a.message}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {a.projectId}
                </span>
                {!a.ackedAt && (
                  <button
                    onClick={() => void handleAck(a.id)}
                    disabled={acking === a.id}
                    style={{
                      marginLeft: 'auto',
                      padding: '1px 7px',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    {acking === a.id ? '…' : t('mark_read')}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showAcked} onChange={(e) => setShowAcked(e.target.checked)} />
          Inclure les lues
        </label>
      </div>
    </aside>
  )
}
